import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography, Container, Grid, Paper, Fade, GlobalStyles } from "@mui/material";
import { getDatabase, ref, get } from "firebase/database";
import { Leaderboard, Groups } from "@mui/icons-material";
import StaffAnalyticsTable from "./StaffAnalyticsTable";

const workstations = ["Receive", "Treat", "Paint", "Pack", "Deliver"];
const dateFields = {
  Receive: "receivingDate",
  Treat: "dateTreat",
  Paint: "datePaint",
  Pack: "datePack",
  Deliver: "dateDeliver",
};

const AnalyticStaff = ({ onBack }) => {
  const [analyticsList, setAnalyticsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hint, setHint] = useState("");

  useEffect(() => {
    const db = getDatabase();

    const fetchAnalytics = async () => {
      const tempStats = {};
      const today = new Date();
      
      const dayOfWeek = today.getDay();
      const monday = new Date(today);
      monday.setDate(today.getDate() - ((dayOfWeek + 6) % 7));
      monday.setHours(0, 0, 0, 0);

      const saturday = new Date(monday);
      saturday.setDate(monday.getDate() + 5);
      saturday.setHours(23, 59, 59, 999);

      const targetWeekStrings = new Set();
      for (let i = 0; i <= 5; i++) {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        const dayStr = String(d.getDate()).padStart(2, '0');
        const monthStr = String(d.getMonth() + 1).padStart(2, '0');
        const yearStr = d.getFullYear();
        targetWeekStrings.add(`${dayStr}/${monthStr}/${yearStr}`);
      }

      try {
        const promises = workstations.map(ws => get(ref(db, ws)));
        const snapshots = await Promise.all(promises);

        snapshots.forEach((snapshot, index) => {
          const ws = workstations[index];
          const dateField = dateFields[ws];
          
          if (!snapshot.exists()) return;

          const records = snapshot.val();
          
          Object.keys(records).forEach((key) => {
            const val = records[key];
            if (!val) return;

            const dateRaw = val[dateField];
            if (!dateRaw || !targetWeekStrings.has(dateRaw)) return;

            const user = val.username || val.userName;
            if (!user) return;

            const [d, m, y] = dateRaw.split("/");
            const recordDate = new Date(+y, +m - 1, +d);
            
            if (!tempStats[user]) tempStats[user] = {};
            if (!tempStats[user][ws]) tempStats[user][ws] = new Set();
            
            const dateKey = `${recordDate.getFullYear()}-${recordDate.getMonth() + 1}-${recordDate.getDate()}`;
            tempStats[user][ws].add(dateKey);
          });
        });

        const list = Object.entries(tempStats).map(([staff, wsMap]) => {
          const counts = {};
          const days = {};
          workstations.forEach((ws) => {
            const daySet = wsMap[ws] || new Set();
            const sortedDays = [...daySet].sort();
            counts[ws] = sortedDays.length;
            days[ws] = sortedDays;
          });
          return { staffName: staff, counts, days };
        });

        setAnalyticsList(list);
      } catch (error) {
        console.error("Data fetch error:", error);
      } finally {
        setLoading(false);
        const todayDow = today.getDay();
        setHint(todayDow >= 1 && todayDow <= 6 ? `Week Progress: Day ${todayDow}` : "Weekend / Sunday View");
      }
    };

    fetchAnalytics();
  }, []);

  return (
    <Box 
      sx={{ 
        minHeight: "100vh",
        p: { xs: 2, sm: 4 },
        pb: { xs: '100px', md: 4 },
        display: "flex",
        justifyContent: "center",
      }}
    >
      <GlobalStyles styles={{
        '.back-btn-staff-wow': {
          backgroundColor: '#a3a3a3dc',
          color: '#fff',
          padding: '15px 30px',
          border: 'none',
          borderRadius: '50px',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          zIndex: 1100,
        },
        '.back-btn-staff-wow:hover': {
          backgroundColor: '#777777dc',
          transform: 'translateY(-3px) scale(1.05)',
          boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
        },
        '.btn-icon-staff': {
          transition: 'transform 0.3s ease',
          fontSize: '20px',
        },
        '.back-btn-staff-wow:hover .btn-icon-staff': {
          transform: 'translateX(-4px)',
        },
        '@media (max-width: 600px)': {
          '.back-btn-staff-wow': {
            position: 'fixed',
            bottom: '20px',
            left: '20px',
            width: '50px',
            height: '50px',
            padding: '0',
            borderRadius: '50%',
          },
          '.btn-text-staff': {
            display: 'none',
          }
        }
      }} />

      <Container maxWidth="xl">
        {/* Header Block */}
        <Box sx={{ mb: { xs: 3, md: 5 }, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
          <Typography variant="h3" sx={{ fontWeight: 700, color: "#0d47a1", fontSize: "40px" }}>
            Staff Placement
          </Typography>
          <Typography sx={{ color: "#5c6bc0", fontWeight: 500, opacity: 0.8, display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1, fontSize: { xs: '0.85rem', md: '1.2rem' } }}>
            {hint}
          </Typography>
        </Box>

        {/* Top Metric Cards - Hidden during loading to prevent empty layouts */}
        {!loading && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 3, borderRadius: 4, display: 'flex', alignItems: 'center', gap: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <Groups sx={{ fontSize: 40, color: '#1e88e5' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold">{analyticsList.length}</Typography>
                  <Typography variant="body2" color="text.secondary">Active Staff</Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 3, borderRadius: 4, display: 'flex', alignItems: 'center', gap: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <Leaderboard sx={{ fontSize: 40, color: '#43a047' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold">Mon-Sat</Typography>
                  <Typography variant="body2" color="text.secondary">Tracking Period</Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* --- MAIN CONTENT AREA FIX --- */}
        {loading ? (
          /* Show this instantly when loading is true */
          <Box sx={{ display: "flex", flexDirection: 'column', alignItems: "center", py: 10 }}>
            <CircularProgress thickness={5} size={60} sx={{ color: '#1a237e' }} />
            <Typography sx={{ mt: 2, color: '#1a237e', fontWeight: 600 }}>
              Analyzing data...
            </Typography>
          </Box>
        ) : (
          /* Smoothly fade in the analytics container once loading is done */
          <Fade in={!loading} timeout={800}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 1, md: 3 }, 
                borderRadius: 6, 
                background: "rgba(255, 255, 255, 0.6)", 
                backdropFilter: "blur(12px)", 
                border: "1px solid rgba(255, 255, 255, 0.3)", 
                boxShadow: "0 10px 40px rgba(0,0,0,0.04)", 
                overflow: "hidden" 
              }}
            >
              <Box sx={{ width: "100%", overflowX: "auto" }}>
                <StaffAnalyticsTable analyticsList={analyticsList} />
              </Box>
            </Paper>
          </Fade>
        )}
        {/* --- END CONTENT AREA FIX --- */}

        {/* Navigation Action */}
        <Box sx={{ mt: 4, display: "flex", justifyContent: { xs: "center", md: "flex-start" } }}>
          <button className="back-btn-staff-wow" onClick={onBack}>
            <span className="btn-icon-staff">←</span>
            <span className="btn-text-staff">Back</span>
          </button>
        </Box>
      </Container>
    </Box>
  );
};

export default AnalyticStaff;